/**
 * @fileoverview View User actions.
 * @description The actions the backend allows the caller on the viewed user (Edit, Resend
 * invitation, Reactivate, Deactivate), from the user's `allowedActions`. Each hands over to the
 * page. Resend also needs the user's pending invitation id.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import type { UserAllowedAction, UserDetail } from '@/features/users/types/users.types'

interface ViewUserActionsProps {
  user: UserDetail
  onEdit: () => void
  onDeactivate: () => void
  onReactivate: () => void
  /** Resends this user's pending invitation. */
  onResendInvitation: (user: UserDetail) => void
}

const ACTION_BUTTON_CLASS =
  'rounded-md border border-input-border bg-white px-2.5 py-1.5 text-xs font-semibold text-primary hover:border-primary'
const DANGER_ACTION_BUTTON_CLASS =
  'rounded-md border border-input-border bg-white px-2.5 py-1.5 text-xs font-semibold text-danger hover:border-danger'

/**
 * @component
 * @param {ViewUserActionsProps} props - The user and the action callbacks.
 * @param {UserDetail} props.user - The viewed user.
 * @param {() => void} props.onEdit - Opens the Edit User dialog for this user.
 * @param {() => void} props.onDeactivate - Opens the Deactivate User dialog for this user.
 * @param {() => void} props.onReactivate - Opens the Reactivate User dialog for this user.
 * @param {(user: UserDetail) => void} props.onResendInvitation - Resends the pending invitation.
 */
function ViewUserActions({
  user,
  onEdit,
  onDeactivate,
  onReactivate,
  onResendInvitation,
}: ViewUserActionsProps) {
  const { t } = useTranslation()

  /**
   * @description Whether the backend allows the viewer this action on the user.
   * @param {UserAllowedAction} action - The action to check.
   * @returns {boolean} True when `allowedActions` lists it.
   */
  const allows = (action: UserAllowedAction) => user.allowedActions.includes(action)

  /**
   * @description Resends the user's pending invitation.
   */
  const resendInvitation = () => {
    onResendInvitation(user)
  }

  if (user.allowedActions.length === 0) {
    return null
  }

  return (
    <div className="flex flex-wrap justify-end gap-2" data-testid="view-user-actions">
      {allows('EDIT') && (
        <button
          type="button"
          onClick={onEdit}
          data-testid="view-user-edit-button"
          className={ACTION_BUTTON_CLASS}
        >
          {t('sales.userManagement.edit')}
        </button>
      )}
      {allows('RESEND_INVITATION') && user.pendingInvitationId !== null && (
        <button
          type="button"
          onClick={resendInvitation}
          data-testid="view-user-resend-invitation-button"
          className={ACTION_BUTTON_CLASS}
        >
          {t('sales.userManagement.resendInvitation')}
        </button>
      )}
      {allows('REACTIVATE') && (
        <button
          type="button"
          onClick={onReactivate}
          data-testid="view-user-reactivate-button"
          className={ACTION_BUTTON_CLASS}
        >
          {t('sales.userManagement.reactivate')}
        </button>
      )}
      {allows('DEACTIVATE') && (
        <button
          type="button"
          onClick={onDeactivate}
          data-testid="view-user-deactivate-button"
          className={DANGER_ACTION_BUTTON_CLASS}
        >
          {t('sales.userManagement.deactivate')}
        </button>
      )}
    </div>
  )
}

export default ViewUserActions
